/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import { ButtonIcon, HelperText, Label, LinearProgress, List, ListItem, ListItemText, ModalSample, Notify, Popover, Popup, ScrollBar, Slider, SliderItem, Typography } from "../..";
import { getGlobal } from "../../../global";
import { Another, Compressed, Image, Pdf, Presentation, Sheet, Word } from "../../../icons";
import PropTypes from 'prop-types';
import { forwardRef, memo, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { bgColor, border, borderRadius4px, borderRadius50, bottom, boxBorder, boxContent, cursorPointer, displayBlock, displayFlex, displayInlineBlock, displayNone, flexCol, flexRow, flexWrap, floatRight, gap, itemsCenter, justifyBetween, justifyCenter, justifyEnd, justifyStart, left, mg, mgb, mgl, mgr, mgx, parseHeight, parseMaxWidth, parseMaxWidthHeight, parseMinWidth, parseWidth, parseWidthHeight, pd, positionAbsolute, positionRelative, textCenter, textColor, textRight, top, truncate, userSelectNone } from "../../../styles/general";
import { useTheme } from "../../../theme";
import { classNames, date as moment, getFileType } from "../../../utils";
const {
  colors: {
    line: {
      normal: lineNormal
    }
  }
} = useTheme();
const icons = {
  pdf: jsx(Pdf, {
    width: '100%',
    height: '100%'
  }),
  word: jsx(Word, {
    width: '100%',
    height: '100%'
  }),
  sheet: jsx(Sheet, {
    width: '100%',
    height: '100%'
  }),
  presentation: jsx(Presentation, {
    width: '100%',
    height: '100%'
  }),
  image: jsx(Image, {
    width: '100%',
    height: '100%'
  }),
  compressed: jsx(Compressed, {
    width: '100%',
    height: '100%'
  }),
  default: jsx(Another, {
    width: '100%',
    height: '100%'
  })
};
const oldAttached = [];
const allNewAttached = new FormData();
const attached = []; // attached = [...dataState, ...newDataState]
const chosenItems = [];
const getType = data => {
  if (data.type !== undefined) {
    var _type$match;
    const pattern = /word|sheet|presentation|pdf|image|compressed/i;
    let type = data.type;
    if (!type) {
      if (/rar/.test(data.name)) {
        type = 'compressed';
      }
    }
    return ((_type$match = type.match(pattern)) === null || _type$match === void 0 ? void 0 : _type$match[0]) || 'default';
  } else {
    var _data$FileName;
    const FileExt = (_data$FileName = data.FileName) === null || _data$FileName === void 0 ? void 0 : _data$FileName.slice(data.FileName.lastIndexOf('.'));
    return getFileType(FileExt, 'default');
  }
};
const getDateTime = date => {
  if (String(date) === 'Invalid Date') return 'unknown';
  return moment(date).format('DD/MM/YYYY, HH:mm:ss');
};
const getNewUnitSize = (sizeName, totalSize, num) => {
  if (sizeName === 'B' && num === 1 || sizeName === 'TB' && num === -1) return [sizeName, totalSize];
  const arraySize = ['TB', 'GB', 'MB', 'KB', 'B'];
  const index = arraySize.indexOf(sizeName);
  const newTotalSize = totalSize * Math.pow(1024, num);
  // newTotalSize = Math.round(newTotalSize*100)/100;
  return [arraySize[index + num], newTotalSize];
};
const getBit = (size, unit) => {
  switch (true) {
    case /^B$/i.test(unit):
      return size * 1;
    case /KB/i.test(unit):
      return size * 1024;
    case /MB/i.test(unit):
      return size * 1024 ** 2;
    case /GB/i.test(unit):
      return size * 1024 ** 3;
    case /TB/i.test(unit):
      return size * 1024 ** 4;
    default:
      return size * 1024 * 1024;
  }
};
const formatBytes = (bytes, decimals = 2) => {
  if (!+bytes) return '0 Bytes';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  // return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`
  return [parseFloat((bytes / Math.pow(k, i)).toFixed(dm)), sizes[i]];
};
const divideSizeMap = new Map([['B', 1], ['KB', 1024], ['MB', 1024 ** 2], ['GB', 1024 ** 3], ['TB', 1024 ** 4]]);
const Attachment = /*#__PURE__*/memo( /*#__PURE__*/forwardRef(({
  accept,
  allowSort,
  className,
  data,
  deleteNotifyText,
  disabled,
  domain,
  error,
  hintText,
  id,
  inputProps,
  isStripDomain,
  label,
  maxFile,
  maxSize: maxSizeProp,
  multiple,
  onChange,
  onDownload: onDownloadProp,
  onRemove: onRemoveProp,
  onView: onViewProp,
  ownerName,
  readOnly,
  //deprecated
  required,
  style,
  unitSize,
  uploadErrorInfo,
  viewType
}, reference) => {
  if (!reference) reference = useRef(null);
  const ref = useRef(null);
  const attachTextRef = useRef(null);
  const attachmentHandleIconRef = useRef(null);
  const attachmentInputRef = useRef(null);
  const attachedRef = useRef(null);
  // const newAttachedRef = useRef([]);
  const popupRef = useRef(null);
  const popoverRef = useRef(null);
  const isDeleteAll = useRef(false);
  const notifyRef = useRef(null);
  const removedAttachedRef = useRef([]);
  const prevent = useRef(null);
  const existClickOutOfItem = useRef(null);
  const maxSize = useRef(Infinity);
  const timer = useRef(null);
  const [showModal, setShowModal] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [viewTypeState, setViewTypeState] = useState(viewType);
  const [newDataState, setNewDataState] = useState([]);
  const [dataState, setDataState] = useState([]);
  const isEnable = !readOnly && !disabled;
  const isEmpty = !((dataState === null || dataState === void 0 ? void 0 : dataState.length) + (newDataState === null || newDataState === void 0 ? void 0 : newDataState.length));
  const _AttachmentRootCSS = AttachmentRootCSS(isEmpty, isEnable);

  // Start handler
  const onSortElement = (e, type) => {
    let multi = 1;
    const currentActive = popoverRef.current.querySelector('.DGN-Active');
    if (currentActive) {
      if (e.currentTarget.classList.contains('DGN-Active')) {
        if (e.currentTarget.classList.contains('DGN-Decrease')) {
          e.currentTarget.classList.remove('DGN-Decrease');
        } else {
          multi = -1;
          e.currentTarget.classList.add('DGN-Decrease');
        }
      } else {
        currentActive.classList.remove('DGN-Decrease');
        currentActive.classList.remove('DGN-Active');
      }
    }
    e.currentTarget.classList.add('DGN-Active');
    const attachedList = Array.from(attachedRef.current.querySelectorAll('.attachment-row'));
    attachedList.sort((a, b) => {
      let textA = a.querySelector('.' + (type === 'filesize' ? 'filename' : type)).textContent;
      let textB = b.querySelector('.' + (type === 'filesize' ? 'filename' : type)).textContent;
      if (type === 'datetime') {
        textA = textA.replace(/.{10}/, text => moment(text, 'DD/MM/YYYY').format('YYYY/MM/DD'));
        textB = textB.replace(/.{10}/, text => moment(text, 'DD/MM/YYYY').format('YYYY/MM/DD'));
      } else if (type === 'filesize') {
        const objA = attached.find(attach => attach.FileName === textA);
        const objB = attached.find(attach => attach.FileName === textB);
        textA = (objA.size || objA.FileSize).toString();
        textB = (objB.size || objB.FileSize).toString();
      }
      return multi * textA.localeCompare(textB, 'en', {
        numeric: type === 'filesize'
      });
    });
    attached.sort((a, b) => {
      let valueA, valueB;
      if (type === 'filesize' || type === 'datetime') {
        if (type === 'filesize') {
          valueA = a.size || a.FileSize;
          valueB = b.size || b.FileSize;
        } else {
          valueA = new Date(a.CreateDate).getTime();
          valueB = new Date(b.CreateDate).getTime();
        }
      } else if (type === 'filetype') {
        // filetype
        valueA = getType(a);
        valueB = getType(b);
      } else {
        valueA = a[type === 'filename' ? 'FileName' : 'UserName'] || ownerName;
        valueB = b[type === 'filename' ? 'FileName' : 'UserName'] || ownerName;
      }
      return multi * valueA.toString().localeCompare(valueB.toString(), 'en', {
        numeric: type === 'filesize' || type === 'datetime'
      });
    });
    attachedList.forEach(el => {
      attachedRef.current.appendChild(el);
    });
  };

  // const afterChangeFile = (length = 0) => {
  //     setIsEmpty(!length);
  // };

  const onChangeFiles = async e => {
    var _e$target, _e$target$files;
    if (!isEnable) return;
    e.persist();
    if (e !== null && e !== void 0 && (_e$target = e.target) !== null && _e$target !== void 0 && (_e$target$files = _e$target.files) !== null && _e$target$files !== void 0 && _e$target$files.length) {
      let files = e.target.files;
      const lengthAttached = attached.length;
      for (let i = 0; i < files.length; i++) {
        if (i + 1 > maxFile - lengthAttached || files[i].size > maxSize.current || checkExistingFile(files[i]) || !checkAcceptFileType(files[i])) {
          if (i + 1 > maxFile - lengthAttached) {
            var _files$i;
            notifyRef.current.instance.show(`${(_files$i = files[i]) === null || _files$i === void 0 ? void 0 : _files$i.name} - ${uploadErrorInfo['maxFile']}`);
          } else if (files[i].size > maxSize.current) {
            var _files$i2;
            notifyRef.current.instance.show(`${(_files$i2 = files[i]) === null || _files$i2 === void 0 ? void 0 : _files$i2.name} - ${uploadErrorInfo['maxSize']}`);
          } else if (!checkAcceptFileType(files[i])) {
            var _files$i3;
            notifyRef.current.instance.show(`${(_files$i3 = files[i]) === null || _files$i3 === void 0 ? void 0 : _files$i3.name} - ${uploadErrorInfo['fileType']}`);
          } else {
            var _files$i4;
            notifyRef.current.instance.show(`${(_files$i4 = files[i]) === null || _files$i4 === void 0 ? void 0 : _files$i4.name} - ${uploadErrorInfo['existingFile']}`);
          }
          files = removeFileOutInputFiles(i, true);
          i--;
        }
      }
      const length = files.length;
      if (!lengthAttached && !length) {
        // afterChangeFile();
        return;
      }
      // afterChangeFile(lengthAttached + length);
      if (multiple || inputProps && inputProps.multiple) {
        for (let i = 0; i < length; i++) {
          files[i].FileName = files[i].name;
          attached.push(files[i]);
        }
        setNewDataState([...newDataState, ...Array.from(files)]);
        for (let i = 0; i < length; i++) {
          files[i].URL = await onProgressing(files[i]);
        }
      } else if (length) {
        files[0].FileName = files[0].name;
        setNewDataState([...newDataState, files[0]]);
        attached.push(files[0]);
        files[0].URL = await onProgressing(files[0]);
      }
      insertAttached(files);
      stripDomain();
      if (onChange) onChange({
        element: e.target,
        attached,
        oldAttached,
        newAttached: files,
        allNewAttached: allNewAttached.getAll('files')
      });
    }
    // else if (!attached.length) {
    //     afterChangeFile();
    // }
  };

  const onProgressing = file => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      let url;
      reader.readAsDataURL(file);
      reader.onload = e => {
        url = e.target.result;
      };
      reader.onloadstart = () => {
        attachedRef.current.querySelector(`.css-${AttachedLinearCSS.name}-attached`).childNodes[0].style.width = '0%';
      };
      reader.onprogress = e => {
        attachedRef.current.querySelector(`.css-${AttachedLinearCSS.name}-attached`).childNodes[0].style.width = e.loaded / e.total * 100 + '%';
      };
      reader.onloadend = () => {
        setTimeout(() => {
          attachedRef.current.querySelector(`.css-${AttachedLinearCSS.name}-attached`).parentNode.remove();
          resolve(url);
        }, 200);
      };
      reader.onerror = () => {
        console.log('Upload file fail');
        reject(false);
      };
    });
  };
  const calculateSize = size => {
    if (!size && size !== 0) return 'unknown';
    let totalSize;
    let unitSizeName;
    const divideSize = unitSize ? divideSizeMap.get(unitSize.toUpperCase()) : null;
    if (divideSize) {
      totalSize = size / divideSize;
      unitSizeName = unitSize.toUpperCase();
      while (totalSize < 0.01 && unitSizeName !== 'B') {
        [unitSizeName, totalSize] = getNewUnitSize(unitSizeName, totalSize, 1);
      }
    } else {
      // auto-detect unit size name
      [totalSize, unitSizeName] = formatBytes(size);
    }
    if (totalSize < 1000) {
      totalSize = Math.round(totalSize * 100) / 100;
    } else {
      totalSize = Math.round(totalSize);
    }
    return totalSize + ' ' + unitSizeName;
  };
  const onShowPopup = () => {
    setShowPopup(true);
  };
  const onClosePopup = () => {
    setShowPopup(false);
    if (isDeleteAll.current) {
      isDeleteAll.current = false;
      chosenItems.length = 0;
      removeActiveChosenItem();
    }
  };
  const onDownloadMultiple = () => {
    const index = chosenItems[0];
    let file;
    if (index < oldAttached.length) {
      file = mountDomain(oldAttached[index]);
      onDownload(file.URL, file.FileName);
    } else {
      const files = allNewAttached.getAll('files');
      file = mountDomain(files[index - oldAttached.length]);
      onDownload(file.URL, file.name);
    }
  };
  const onRemoveMultiple = () => {
    const attachedItems = [...dataState, ...newDataState];
    const nodeList = attachedRef.current.querySelectorAll('.chosen');
    const recentRemovedAttached = [];
    const tempDataState = [...dataState];
    const tempNewDataState = [...newDataState];
    nodeList.forEach(itemEl => {
      const parent = itemEl.parentNode;
      const index = Array.from(attachedRef.current.children).indexOf(parent);
      const deletedFile = attachedItems[index];
      removedAttachedRef.current.push(deletedFile);
      recentRemovedAttached.push(deletedFile);
      if (Object.prototype.toString.call(deletedFile) === '[object File]' && tempNewDataState !== null && tempNewDataState !== void 0 && tempNewDataState.length) {
        removeFileOutInputFiles(tempNewDataState.findIndex(attach => (attach === null || attach === void 0 ? void 0 : attach.FileName) === (deletedFile === null || deletedFile === void 0 ? void 0 : deletedFile.FileName) && (attach === null || attach === void 0 ? void 0 : attach.CreateDate) === (deletedFile === null || deletedFile === void 0 ? void 0 : deletedFile.CreateDate)));
        tempNewDataState.splice(tempNewDataState.findIndex(attach => (attach === null || attach === void 0 ? void 0 : attach.FileName) === (deletedFile === null || deletedFile === void 0 ? void 0 : deletedFile.FileName) && (attach === null || attach === void 0 ? void 0 : attach.CreateDate) === (deletedFile === null || deletedFile === void 0 ? void 0 : deletedFile.CreateDate)), 1);
        setNewDataState(tempNewDataState);
      } else {
        oldAttached.splice(oldAttached.findIndex(attach => (attach === null || attach === void 0 ? void 0 : attach.FileName) === (deletedFile === null || deletedFile === void 0 ? void 0 : deletedFile.FileName) && (attach === null || attach === void 0 ? void 0 : attach.CreateDate) === (deletedFile === null || deletedFile === void 0 ? void 0 : deletedFile.CreateDate)), 1);
        tempDataState.splice(tempDataState.findIndex(attach => (attach === null || attach === void 0 ? void 0 : attach.FileName) === (deletedFile === null || deletedFile === void 0 ? void 0 : deletedFile.FileName) && (attach === null || attach === void 0 ? void 0 : attach.CreateDate) === (deletedFile === null || deletedFile === void 0 ? void 0 : deletedFile.CreateDate)), 1);
        setDataState(tempDataState);
      }
    });
    const tempAttachedItems = [...tempDataState, ...tempNewDataState];
    // if (tempAttachedItems.length < 2) {
    //     afterChangeFile(tempAttachedItems?.length || 0);
    // }
    stripDomain();
    if (onChange) onChange({
      element: nodeList,
      attached: tempAttachedItems,
      recentRemovedAttached,
      removedAttached: removedAttachedRef.current,
      oldAttached,
      allNewAttached: allNewAttached.getAll('files')
    });
    !!onRemoveProp && onRemoveProp(recentRemovedAttached);
    onClosePopup();
  };
  const clearAllAttached = (confirmPopup = false) => {
    const allAttachEl = attachedRef.current.querySelectorAll('.attachment-row');
    if (allAttachEl && allAttachEl.length) {
      allAttachEl.forEach(attachEl => attachEl.firstChild.classList.add('chosen'));
      isDeleteAll.current = true;
      confirmPopup ? onShowPopup() : onRemoveMultiple();
    }
  };
  const onGetViewItem = (i = 0) => {
    const index = chosenItems[i];
    const node = attachedRef.current.childNodes[index];
    const AttachmentID = attached[index].AttachmentID;
    onView(node, AttachmentID);
  };
  const onView = (node, AttachmentID) => {
    if (timer.current) {
      prevent.current = true;
      clearTimeout(timer.current);
    }
    const index = Array.from(attachedRef.current.children).indexOf(node);
    if (onViewProp) onViewProp(AttachmentID, index);else {
      setShowModal(true);
    }
    setTimeout(() => {
      prevent.current = false;
    }, 110);
  };
  const onDownload = (url, name) => {
    if (!url) return;
    fetch(url).then(resp => resp.blob()).then(blob => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      // the filename you want
      a.download = name;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
    }).catch(() => alert('File not found'));
    if (onDownloadProp) onDownloadProp(url);
  };
  const removeFileOutInputFiles = (index, keeping) => {
    const dt = new DataTransfer();
    for (let file of attachmentInputRef.current.files) {
      if (file !== attachmentInputRef.current.files[index]) {
        dt.items.add(file);
      }
    }
    if (!keeping) {
      const files = allNewAttached.getAll('files');
      files.splice(index, 1);
      allNewAttached.delete('files');
      files.forEach(file => allNewAttached.append('files', file));
      // newAttachedRef.current = files || [];
      // setNewDataState(files || []);
    }

    attachmentInputRef.current.files = dt.files;
    return dt.files;
  };
  const insertAttached = files => {
    if (multiple || inputProps && inputProps.multiple) {
      let tempArr = [];
      for (let file of files) {
        allNewAttached.append('files', file);
        // newAttachedRef.current.push(file);
        tempArr.push(file);
      }
      setNewDataState([...newDataState, ...tempArr]);
    } else if (files[0]) {
      allNewAttached.delete('files');
      allNewAttached.append('files', files[0]);
      // newAttachedRef.current = [files[0]];
      setNewDataState([files[0]]);
    }
    // attachmentInputRef.current.files = allNewAttached.getAll('files');
  };

  const checkExistingFile = newFile => {
    const files = [...dataState, ...newDataState];
    return files.some(file => ((file === null || file === void 0 ? void 0 : file.FileName) || (file === null || file === void 0 ? void 0 : file.name)) === ((newFile === null || newFile === void 0 ? void 0 : newFile.FileName) || (newFile === null || newFile === void 0 ? void 0 : newFile.name)) && ((file === null || file === void 0 ? void 0 : file.FileSize) || (file === null || file === void 0 ? void 0 : file.size)) === ((newFile === null || newFile === void 0 ? void 0 : newFile.FileSize) || (newFile === null || newFile === void 0 ? void 0 : newFile.size)));
    // const files = allNewAttached.getAll('files');
    // if (files && files.length) {
    //     const length = files.length;
    //     for (let i = 0; i < length; i++) {
    //         if (parseStringify(newFile) === parseStringify(files[i])) return true;
    //     }
    // }
    // return false;
  };

  const checkAcceptFileType = newFile => {
    if (!accept || !accept.length) return true;
    const type = typeof accept === 'string' ? accept : accept.join('-');
    if (typeof accept === 'string') {
      const acceptType = type.replace('/*', '');
      return newFile.type.includes(acceptType);
    }
    const extFile = newFile.name.slice(newFile.name.lastIndexOf('.') + 1);
    return accept.join('-').includes(extFile);
  };
  const onChooseItem = e => {
    const ctrlKey = e.ctrlKey;
    const shiftKey = e.shiftKey;
    const itemEl = e.currentTarget;
    timer.current = setTimeout(() => {
      if (!prevent.current) {
        const index = Array.from(attachedRef.current.children).indexOf(itemEl.parentNode);
        if (shiftKey && !ctrlKey) {
          let startIndex = chosenItems[chosenItems.length - 1] || 0;
          let endIndex = index;
          if (startIndex > endIndex) {
            endIndex = startIndex;
            startIndex = index;
          }
          chosenItems.length = 0;
          attachedRef.current.querySelectorAll(`.css-${AttachedItemCSS.name}`).forEach((itemEl, indexEl) => {
            if (startIndex <= indexEl && indexEl <= endIndex) {
              if (!itemEl.classList.contains('chosen')) {
                itemEl.classList.add('chosen');
              }
              chosenItems.push(indexEl);
            } else if (itemEl.classList.contains('chosen')) {
              itemEl.classList.remove('chosen');
            }
          });
          if (chosenItems[chosenItems.length - 1] !== index) {
            chosenItems.shift();
            chosenItems.push(index);
          }
        } else if (!ctrlKey) {
          chosenItems.length = 0;
          removeActiveChosenItem();
          chosenItems.push(index);
          itemEl.classList.add('chosen');
        } else if (itemEl.classList.contains('chosen')) {
          chosenItems.splice(chosenItems.indexOf(index));
          itemEl.classList.remove('chosen');
          if (!chosenItems.length) {
            setTimeout(() => {
              removeAllChosenItems();
            }, 0);
          }
        } else {
          chosenItems.push(index);
          itemEl.classList.add('chosen');
        }
        // Add event listener click out of item
        if (!existClickOutOfItem.current) {
          existClickOutOfItem.current = true;
          attachmentHandleIconRef.current.style.display = 'block';
          document.addEventListener('keydown', onKeyDown);
          document.addEventListener('click', clickOutOfItem);
        }
        checkChosenMultiple();
      }
      prevent.current = false;
    }, 100);
  };
  const removeActiveChosenItem = () => {
    attachedRef.current.querySelectorAll('.chosen').forEach(itemEl => {
      itemEl.classList.remove('chosen');
    });
  };
  const checkChosenMultiple = () => {
    const actionIcons = attachmentHandleIconRef.current.childNodes;
    if (chosenItems.length > 1 && actionIcons[0].style.display !== 'none') {
      actionIcons[0].style.display = 'none';
      actionIcons[1].style.display = 'none';
    } else if (chosenItems.length === 1 && actionIcons[0].style.display === 'none') {
      actionIcons[0].style.display = null;
      actionIcons[1].style.display = null;
    }
  };
  const onKeyDown = event => {
    const key = event.key;
    if (key === 'Delete') {
      // Press Delete key to delete item(s)
      onShowPopup();
    } else if (key === 'Enter') {
      if (popupRef.current.style.display !== 'flex') {
        onGetViewItem(chosenItems.length - 1);
      }
    } else if (/a/i.test(key) && event.ctrlKey) {
      chosenItems.length = 0;
      const allAttachEl = attachedRef.current.querySelectorAll('.attachment-row');
      if (allAttachEl && allAttachEl.length) {
        allAttachEl.forEach((attachEl, index) => chosenItems.push(index) & attachEl.firstChild.classList.add('chosen'));
      }
      event.preventDefault();
      return false;
    } else if (/Arrow/.test(key)) {
      // Press Arrow key to select another item
      event.preventDefault();
      const itemsNode = attachedRef.current.querySelectorAll('.attachment-row');
      const chosenItemsNode = Array.from(itemsNode).filter(el => el.firstChild.classList.contains('chosen'));
      const length = itemsNode.length;
      let index;
      if (key === 'ArrowDown' || key === 'ArrowRight') {
        for (let id = length - 1; id >= 0; id--) {
          if (Array.from(itemsNode)[id].firstChild.classList.contains('chosen')) {
            index = id;
            break;
          }
        }
        if (index === length - 1) {
          if (chosenItemsNode.length !== 1 && !event.shiftKey) {
            chosenItemsNode.forEach((node, i) => {
              if (i !== index) {
                node.firstChild.classList.remove('chosen');
              }
            });
            chosenItems.length = 0;
            chosenItems.push(index);
            Array.from(itemsNode)[index].scrollIntoView({
              block: 'center',
              behavior: 'smooth'
            });
          }
          return;
        }
        Array.from(itemsNode)[index + 1].scrollIntoView({
          block: 'center',
          behavior: 'smooth'
        });
        if (!event.shiftKey) {
          itemsNode.forEach(node => {
            node.firstChild.classList.remove('chosen');
          });
        }
        itemsNode[index + 1].firstChild.classList.add('chosen');
        chosenItems.push(index + 1);
      } else if (key === 'ArrowUp' || key === 'ArrowLeft') {
        index = Array.from(itemsNode).findIndex(el => el.firstChild.classList.contains('chosen'));
        if (index === 0) {
          if (chosenItemsNode.length !== 1 && !event.shiftKey) {
            chosenItemsNode.forEach((node, i) => {
              if (i !== index) {
                node.firstChild.classList.remove('chosen');
              }
            });
            chosenItems.length = 0;
            chosenItems.push(index);
            Array.from(itemsNode)[index].scrollIntoView({
              block: 'center',
              behavior: 'smooth'
            });
          }
          return;
        }
        Array.from(itemsNode)[index - 1].scrollIntoView({
          block: 'center',
          behavior: 'smooth'
        });
        if (!event.shiftKey) {
          chosenItemsNode.forEach(node => {
            node.firstChild.classList.remove('chosen');
          });
        }
        itemsNode[index - 1].firstChild.classList.add('chosen');
        chosenItems.push(index - 1);
      }
      if (!event.shiftKey) {
        chosenItems.length = 1;
      }
    }
  };
  const clickOutOfItem = e => {
    var _popupRef$current, _popupRef$current$sty;
    if (!attachedRef || !attachedRef.current) {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('click', clickOutOfItem);
      return;
    }
    if (!(attachedRef.current.contains(e.target) && attachedRef.current !== e.target) && !attachmentHandleIconRef.current.contains(e.target) && !isDeleteAll.current && (((_popupRef$current = popupRef.current) === null || _popupRef$current === void 0 ? void 0 : (_popupRef$current$sty = _popupRef$current.style) === null || _popupRef$current$sty === void 0 ? void 0 : _popupRef$current$sty.display) !== 'flex' || popupRef.current.querySelector('button.filled.info').contains(e.target))) {
      removeAllChosenItems();
      removeActiveChosenItem();
    }
  };
  const removeAllChosenItems = () => {
    chosenItems.length = 0;
    existClickOutOfItem.current = false;
    attachmentHandleIconRef.current.style.display = null;
    document.removeEventListener('keydown', onKeyDown);
    document.removeEventListener('click', clickOutOfItem);
  };

  // const parseStringify = fileInfo =>
  //     JSON.stringify({
  //         lastModified: fileInfo.lastModified,
  //         lastModifiedDate: fileInfo.lastModifiedDate,
  //         name: fileInfo.name,
  //         size: fileInfo.size,
  //         type: fileInfo.type,
  //         webkitRelativePath: fileInfo.webkitRelativePath,
  //     });

  const mountDomain = item => {
    if (!item) return item;
    const cloneItem = JSON.parse(JSON.stringify(item));
    if (domain) {
      if (!/\/$/.test(domain)) {
        domain = domain + '/';
      }
      if (cloneItem !== null && cloneItem !== void 0 && cloneItem.URL) {
        cloneItem.URL = cloneItem.URL.indexOf('http') < 0 ? domain + cloneItem.URL : cloneItem.URL;
      }
    }
    return cloneItem;
  };
  const stripDomain = () => {
    if (isStripDomain && domain) {
      const re = new RegExp(domain + '/?');
      attached.forEach(item => {
        var _item$URL;
        item.URL = (item === null || item === void 0 ? void 0 : (_item$URL = item.URL) === null || _item$URL === void 0 ? void 0 : _item$URL.replace(re, '')) || '';
      });
      oldAttached.forEach(item => {
        var _item$URL2;
        item.URL = (item === null || item === void 0 ? void 0 : (_item$URL2 = item.URL) === null || _item$URL2 === void 0 ? void 0 : _item$URL2.replace(re, '')) || '';
      });
    }
  };
  useEffect(() => {
    if (attached !== null && attached !== void 0 && attached.length) {
      attached.length = 0;
    }
    return () => {
      existClickOutOfItem.current = false;
    };
  }, []);
  useEffect(() => {
    setViewTypeState(viewType);
  }, [viewType]);
  useEffect(() => {
    if (maxSizeProp) {
      if (!isNaN(maxSizeProp)) {
        maxSize.current = maxSizeProp * 1024 * 1024;
      } else {
        const numberSize = maxSizeProp.match(/\d+/g);
        const unitMaxSize = maxSizeProp.match(/\D+/g);
        maxSize.current = getBit(numberSize, unitMaxSize);
      }
    }
    attachmentInputRef.current.multiple = multiple;
    if (accept && typeof accept === 'string') {
      attachmentInputRef.current.accept = `${accept}/*`.replace('/*/*', '/*');
    }
    return () => {
      maxSize.current = Infinity;
    };
  }, [maxFile, maxSizeProp, multiple, accept]);
  useEffect(() => {
    oldAttached.length = 0;
    // afterChangeFile((data?.length || 0) + (newDataState?.length || 0));
    if (data) {
      // Load files data was uploaded
      const length = data.length;
      for (let i = 0; i < length; i++) {
        if (Object.prototype.toString.call(data[i]) === '[object File]') {
          allNewAttached.append('files', data[i]);
          // newAttachedRef.current.push(data[i]);
          attached.push(data[i]);
        } else {
          const item = mountDomain(data[i]);
          oldAttached.push(item);
          attached.push(item);
        }
      }
      setDataState(data);
    }
    return () => {
      // if (attachedRef.current) {
      //     attachedRef.current.innerHTML = null;
      // }
      oldAttached.length = 0;
      attached.length = 0;
      chosenItems.length = 0;
      allNewAttached.delete('files');
      if (attachmentInputRef.current) {
        attachmentInputRef.current.value = '';
      }
    };
  }, [data]);
  useEffect(() => {
    setNewDataState([]);
  }, [dataState]);
  useEffect(() => {
    if (inputProps && Object.keys(inputProps).length) {
      Object.keys(inputProps).forEach(name => {
        if (typeof inputProps[name] === 'boolean') {
          attachmentInputRef.current[name] = inputProps[name];
        } else {
          attachmentInputRef.current.setAttribute(name, inputProps[name]);
        }
      });
      return () => {
        if (attachmentInputRef.current) {
          Object.keys(inputProps).forEach(name => {
            if (typeof inputProps[name] === 'boolean') {
              attachmentInputRef.current[name] = false;
            } else {
              attachmentInputRef.current.removeAttribute(name);
            }
          });
        }
      };
    }
  }, [inputProps]);
  const triggerClickInput = () => {
    attachmentInputRef.current.click();
  };
  const onDragFileStart = () => {
    attachmentInputRef.current.parentNode.style.display = 'block';
  };
  const onDropInput = () => {
    attachmentInputRef.current.parentNode.style.display = null;
  };
  // End handler

  useImperativeHandle(reference, () => {
    stripDomain();
    const currentRef = ref.current || {};
    currentRef.element = ref.current;
    const _instance = {
      oldAttached: oldAttached,
      allNewAttached: allNewAttached,
      attached: attached,
      clear: confirmPopup => clearAllAttached(confirmPopup),
      openFileDialog: triggerClickInput
    }; // methods
    _instance.__proto__ = {}; // hidden methods
    currentRef.instance = _instance;
    currentRef.oldAttached = oldAttached;
    currentRef.allNewAttached = allNewAttached;
    currentRef.attached = attached;
    currentRef.clear = confirmPopup => clearAllAttached(confirmPopup);
    currentRef.openFileDialog = triggerClickInput;
    return currentRef;
  });

  // Start view
  const AttachHeaderView = useMemo(() => {
    return jsx("div", {
      css: AttachmentHeaderCSS,
      className: 'DGN-UI-Attachment-Header'
    }, jsx("div", {
      css: AttachmentInfoCSS
    }, jsx(Label, {
      uppercase: true,
      required: required,
      type: 'h5',
      color: 'text/sub',
      ref: attachTextRef
    }, label), error ? jsx(HelperText, null, error) : null), jsx("div", {
      css: AttachmentHeaderIconCSS
    }, jsx("div", {
      css: AttachmentHandleIconCSS,
      ref: attachmentHandleIconRef
    }, jsx(ButtonIcon, {
      viewType: 'ghost',
      className: 'view',
      name: 'view',
      onClick: () => onGetViewItem()
    }), jsx(ButtonIcon, {
      viewType: 'ghost',
      className: 'download',
      name: 'download',
      onClick: onDownloadMultiple
    }), isEnable ? jsx(ButtonIcon, {
      viewType: 'ghost',
      className: 'delete',
      name: 'delete',
      onClick: onShowPopup
    }) : null), jsx("div", {
      css: isEmpty ? AttachmentImageEmptyCSS : AttachmentImageCSS
    }, jsx("div", {
      css: [AttachmentImageCenterCSS, !isEnable && [parseHeight('calc(100% - 32px)'), justifyEnd]]
    }, isEnable ? jsx("div", {
      css: AttachmentImageCenterCSS
    }, jsx(ButtonIcon, {
      css: isEmpty && ButtonAttachmentCSS,
      circular: isEmpty,
      viewType: isEmpty ? 'text' : 'ghost',
      name: 'Attachment',
      size: isEmpty ? 'extraGiant' : 'medium',
      onClick: triggerClickInput
    }), isEmpty ? jsx(Typography, {
      css: userSelectNone,
      type: 'p3',
      color: 'system/rest'
    }, hintText) : null) : isEmpty ? jsx(Typography, {
      css: userSelectNone,
      type: 'p3',
      color: 'system/rest'
    }, getGlobal('noFileText')) : null)), allowSort && !isEmpty ? jsx(Popover, {
      ref: popoverRef,
      anchor: jsx(ButtonIcon, {
        viewType: 'ghost',
        name: 'Sort',
        style: {
          marginRight: 8
        }
      }),
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'right'
      },
      transformOrigin: {
        vertical: 'top',
        horizontal: 'right'
      }
    }, jsx(List, null, jsx(ListItem, {
      onClick: e => onSortElement(e, 'filename')
    }, jsx(ListItemText, null, getGlobal('byName'))), jsx(ListItem, {
      onClick: e => onSortElement(e, 'filetype')
    }, jsx(ListItemText, null, getGlobal('byType'))), jsx(ListItem, {
      onClick: e => onSortElement(e, 'filesize')
    }, jsx(ListItemText, null, getGlobal('bySize'))), jsx(ListItem, {
      onClick: e => onSortElement(e, 'datetime')
    }, jsx(ListItemText, null, getGlobal('byDate'))), jsx(ListItem, {
      onClick: e => onSortElement(e, 'username')
    }, jsx(ListItemText, null, getGlobal('byOwner'))))) : null, !isEmpty ? jsx(ButtonIcon, {
      viewType: 'ghost',
      name: viewTypeState === 'detail' ? 'IconView' : 'ListView',
      onClick: () => setViewTypeState(viewTypeState === 'icon' ? 'detail' : 'icon')
    }) : null));
  }, [allowSort, disabled, domain, error, hintText, label, readOnly, required, isEmpty, viewTypeState]);
  const AttachmentInputView = useMemo(() => {
    return jsx("div", {
      css: AttachmentInputCSS,
      className: 'DGN-UI-Attachment-Input'
    }, jsx("input", {
      type: "file",
      ref: attachmentInputRef,
      title: "",
      onDrop: onDropInput,
      onDragLeave: onDropInput,
      onClick: e => e.target.value = null // Fix not fire onChange when select same file
      ,
      onChange: onChangeFiles,
      ...inputProps
    }));
  }, [accept, disabled, domain, inputProps, isStripDomain, maxFile, multiple, onChange, readOnly, uploadErrorInfo, newDataState]);
  const renderAttachmentItem = (item, idx, isNew = false) => {
    const isViewTypeIcon = viewTypeState === 'icon';
    const date = isNew ? new Date() : new Date(item.CreateDate);
    if (isNew) item.CreateDate = date.toISOString();
    const type = getType(item);
    const name = isNew ? item.name : item.FileName;
    return jsx("div", {
      key: idx,
      className: 'attachment-row'
    }, jsx("div", {
      css: AttachedItemCSS,
      onClick: e => onChooseItem(e),
      onDoubleClick: e => onView(e.currentTarget.parentNode, isNew ? null : item.AttachmentID)
    }, jsx("span", {
      css: AttachedItemInfoCSS
    }, jsx("span", {
      css: AttachedItemInfoIconCSS,
      className: classNames((item.lastModifiedDate || isNew) && 'new-file')
    }, icons[type], jsx("span", {
      className: 'filetype',
      style: {
        display: 'none'
      }
    }, type)), jsx(Typography, {
      className: 'filename',
      css: [isViewTypeIcon ? mg([0, 2]) : mgl([3])],
      type: isViewTypeIcon ? 'p3' : 'p1',
      lineClamp: isViewTypeIcon ? 2 : 1
    }, name)), jsx("span", {
      css: AttachedItemOwnerCSS
    }, !isViewTypeIcon ? jsx("span", {
      css: DetailInfoCSS,
      className: 'detail-info'
    }, jsx(Typography, {
      className: 'username',
      type: 'p2',
      color: 'text/sub'
    }, (item === null || item === void 0 ? void 0 : item.UserName) || item.lastModifiedDate && ownerName || ''), jsx(Typography, {
      className: 'datetime',
      type: 'p2',
      color: 'text/sub'
    }, getDateTime(date)), jsx(Typography, {
      className: 'filesize',
      type: 'p2',
      color: 'text/sub'
    }, calculateSize(item.FileSize || item.size))) : null, isNew ? jsx("span", {
      css: AttachedLinearCSS
    }, jsx(LinearProgress, {
      className: `css-${AttachedLinearCSS.name}-attached`,
      determinate: true,
      percent: 0
    })) : null)));
  };
  const AttachedView = useMemo(() => {
    const isViewTypeIcon = viewTypeState === 'icon';
    return jsx(ScrollBar, {
      css: AttachmentListCSS,
      className: 'DGN-UI-Attachment-List'
    }, jsx("div", {
      css: AttachedCSS,
      ref: attachedRef,
      className: classNames(isViewTypeIcon && 'icon-view')
    }, dataState.map((item, idx) => {
      return renderAttachmentItem(item, idx);
    }), newDataState.map((item, idx) => {
      return renderAttachmentItem(item, (data === null || data === void 0 ? void 0 : data.length) + idx, true);
    })));
  }, [data, onViewProp, unitSize, dataState, newDataState, viewTypeState]);
  const PopupView = useMemo(() => {
    return jsx(Popup, {
      open: showPopup,
      ref: popupRef,
      type: 'yesno',
      description: deleteNotifyText,
      pressESCToClose: true,
      onClose: onClosePopup,
      onConfirm: onRemoveMultiple,
      onCancel: onClosePopup
    });
  }, [deleteNotifyText, onChange, onRemoveProp, showPopup]);
  const ModalView = useMemo(() => {
    if (!chosenItems.length) return setShowModal(false);
    const index = chosenItems[0];
    const data = mountDomain(attached[index]);
    // let data = oldAttached[index] || allNewAttached.getAll('files')[index-oldAttached.length];
    return jsx(ModalSample, {
      open: showModal,
      title: data.FileName,
      onClose: () => setShowModal(false)
    }, jsx(Slider, null, jsx(SliderItem, {
      url: data.URL
    })));
  }, [domain, showModal]);
  // End view

  return jsx("div", {
    ref: ref,
    css: _AttachmentRootCSS,
    id: id,
    className: classNames('DGN-UI-Attachment', className),
    style: style,
    onDragEnter: onDragFileStart
  }, AttachHeaderView, AttachmentInputView, AttachedView, PopupView, ModalView, jsx(Notify, {
    ref: notifyRef,
    progressing: true,
    autoDisappear: true,
    color: 'danger'
  }));
}));
const AttachmentHandleIconCSS = css`
    ${displayNone};
    ${parseHeight(24)};
    ${mgr([2])};
    border-right: 1px solid ${lineNormal};
    button {
        ${mgr([2])};
    }
`;
const AttachmentHeaderCSS = css`
    ${displayFlex};
    ${itemsCenter};
    ${justifyBetween};
    ${boxBorder};
    ${parseWidth('100%')};
    ${pd([4, 4])};
`;
const AttachmentInfoCSS = css`
    ${displayFlex};
    ${gap([2])};
`;
const AttachmentHeaderIconCSS = css`
    ${displayFlex};
    ${itemsCenter};
`;
const AttachmentInputCSS = css`
    ${displayNone};
    ${positionAbsolute};
    ${parseWidthHeight('100%')};
    ${top(0)};
    ${left(0)};
    input {
        ${positionRelative};
        ${parseWidthHeight('100%', 'calc(100% + 20px)')};
        opacity: 0;
    }
`;
const AttachmentImageCenterCSS = css`
    ${displayFlex};
    ${flexCol};
    ${justifyCenter};
    ${itemsCenter};
    ${parseWidthHeight('100%')};
`;
const ButtonAttachmentCSS = css`
    border: 1px dashed ${lineNormal} !important;
    ${mg([0, 'auto', 4])};
`;
const AttachmentImageCSS = css`
    ${mgr([2])};
`;
const AttachmentImageEmptyCSS = css`
    ${positionAbsolute};
    ${parseWidthHeight('100%')};
    ${top(0)};
    ${left(0)};
`;
const AttachedItemInfoCSS = css`
    ${displayFlex};
    ${itemsCenter};
    ${borderRadius4px};
    ${userSelectNone};
    ${parseMinWidth('calc(50% - 10px)')};
    ${mgr([2])};
`;
const AttachedItemInfoIconCSS = css`
    ${displayFlex};
    ${flexRow};
    ${positionRelative};
    ${boxContent};
    ${parseMaxWidthHeight(14, 16)};
    ${pd([1, 1.25])};
    &.new-file:after {
        content: '';
        ${positionAbsolute};
        ${borderRadius50};
        ${parseWidthHeight(4, 4)};
        ${bgColor('semantic/success')};
        ${top(4.5)};
        ${left(-2)};
    }
`;
const AttachedItemOwnerCSS = css`
    ${displayFlex};
    ${positionRelative};
    ${itemsCenter};
    ${userSelectNone};
    ${parseHeight('100%')};
    ${textColor('text/sub')};
`;
const DetailInfoCSS = css`
    ${displayFlex};
    ${parseMinWidth(333)};
    .username {
        ${displayInlineBlock};
        ${truncate};
        ${parseMinWidth(120)};
        ${parseMaxWidth(120)};
    }
    .datetime {
        ${displayInlineBlock};
        ${parseMinWidth('max-content')};
        ${parseMaxWidth('max-content')};
    }
    .filesize {
        ${floatRight};
        ${textRight};
        ${mgl(['auto'])};
    }
`;
const AttachedLinearCSS = css`
    ${displayFlex};
    ${positionAbsolute};
    ${itemsCenter};
    ${bgColor('system/white')};
    ${parseWidthHeight('100%')};
    ${top(0)};
`;
const AttachedItemCSS = css`
    ${displayFlex};
    ${justifyBetween};
    ${positionRelative};
    ${borderRadius4px};
    ${cursorPointer};
    ${parseHeight(24)};
    ${pd([0, 2])};
    transition: padding 0.5s ease;
`;
const AttachedCSS = css`
    ${positionRelative};
    ${parseHeight('100%')};
    ${mgx([2])};
    overflow-y: visible;
    .attachment-row {
        ${displayBlock};
        ${mgb([2])};
        &:last-child {
            ${mgb(0)};
        }
    }
    &.icon-view {
        ${displayFlex};
        ${flexWrap};
        ${justifyBetween};
        &::after {
            content: '';
            flex: auto;
        }
        ${parseMaxWidthHeight('calc(100% - 26px)', 200)};
        ${parseWidth('max-content')};
        ${mgl(0)};
        .attachment-row {
            ${positionRelative};
            ${mgl([4])};
            overflow-y: visible;
        }
        .css-${AttachedItemCSS.name} {
            ${displayBlock};
            ${parseWidthHeight(80)};
            ${pd(0)};
            .css-${AttachedItemInfoCSS.name} {
                ${displayBlock};
                ${positionRelative};
                ${textCenter};
                ${truncate};
                ${parseWidth('max-content')};
                ${parseMaxWidth(80)};
                ${parseMinWidth(70)};
                ${mgx(['auto'])};
                .css-${AttachedItemInfoIconCSS.name} {
                    ${displayBlock};
                    ${parseWidthHeight(40)};
                    ${parseMaxWidthHeight(40)};
                    ${mg([1, 'auto', 0.5])};
                    ${pd(0)};
                    &.new-file:after {
                        ${parseWidthHeight(8)};
                        ${top(-1)};
                        ${left(-7)};
                    }
                }
            }
            .css-${AttachedItemOwnerCSS.name} {
                ${parseHeight('auto')};
                position: inherit;
                .css-${AttachedLinearCSS.name} {
                    ${parseHeight('auto')};
                    ${bottom(0)};
                    top: unset;
                    background: none;
                }
            }
        }
    }
    &:not(.icon-view) {
        ${parseMinWidth(684)};
    }
`;
const AttachmentListCSS = css`
    ${boxBorder};
    ${mgr([1.25])};
    ${pd([0, 1.75, 0])};
    overflow-y: overlay;
`;
const AttachmentRootCSS = (isEmpty, isEnable) => css`
    ${displayFlex};
    ${flexCol};
    ${justifyStart};
    ${positionRelative};
    ${borderRadius4px};
    ${boxBorder};
    ${border(1, 'line/normal')};
    ${parseHeight(isEmpty && !isEnable ? 120 : 280)};
    ${bgColor('system/white')};
    .css-${AttachedCSS.name} {
        .css-${AttachedItemCSS.name} {
            &:hover {
                ${bgColor('fill/hover')};
                .css-${AttachedLinearCSS.name} {
                    ${bgColor('fill/hover')};
                }
            }
            &:active {
                ${bgColor('fill/pressed')};
                .css-${AttachedLinearCSS.name} {
                    ${bgColor('fill/pressed')};
                }
            }
            &.chosen {
                ${bgColor('fill/focus')};
                .css-${AttachedItemInfoCSS.name}, .css-${AttachedItemOwnerCSS.name} {
                    ${bgColor('fill/focus')};
                }
            }
        }
    }
    @media (max-width: 716px) {
        .css-${AttachedCSS.name}:not(.icon-view) {
            ${parseMinWidth(575)};
        }
        .css-${AttachedItemInfoCSS.name} {
            ${parseMaxWidth('calc(40% - 10px)')};
        }
    }
`;
Attachment.defaultProps = {
  deleteNotifyText: getGlobal('deleteNotifyText'),
  hintText: getGlobal('dropFileHere'),
  label: getGlobal('attachText'),
  maxSize: getGlobal('maxSizeUpload'),
  uploadErrorInfo: {
    maxFile: getGlobal(['errorDefault', 'maxFile']),
    maxSize: getGlobal(['errorDefault', 'maxSize']),
    fileType: getGlobal(['errorDefault', 'fileType']),
    existingFile: getGlobal(['errorDefault', 'existingFile'])
  },
  ...getGlobal(['components', 'attachment', 'defaultProps'])
};
Attachment.propTypes = {
  /** File types that can be accepted. */
  accept: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  /** If `true`, allow sort. */
  allowSort: PropTypes.bool,
  /** Class for component. */
  className: PropTypes.string,
  /** List attachment:<br />
   * [{<br />
   * &nbsp;&nbsp;&nbsp;&nbsp;"AttachmentID": "ATT2U8O7YPTF1KSNCW3X",<br />
   * &nbsp;&nbsp;&nbsp;&nbsp;"URL": "https://apricot.diginet.com.vn/cdn-dev/file/8a07bee1eeff17a14eee.jpg?path=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJmaWxlUGF0aCI6Ii8yMDIwLzA4LzQ0YjNhYjQ4LTMwNGQtNDQ3NC05ZDk0LWNkNGVlMDQwOGNlMy5qcGciLCJmaWxlTmFtZSI6IjhhMDdiZWUxZWVmZjE3YTE0ZWVlLmpwZyIsImZpbGVTaXplIjoyNDg4MzcsImZpbGVUeXBlIjoiaW1hZ2UvanBlZyIsImlhdCI6MTU5ODI0NDMwMiwiZXhwIjo4Nzk5ODI0NDMwMn0.fow6O5fp2z3vieA9gom5RRwQ7xxwBx9_7X2Fe8T2YI0",<br />
   * &nbsp;&nbsp;&nbsp;&nbsp;"FileName": "8a07bee1eeff17a14eee.jpg",<br />
   * &nbsp;&nbsp;&nbsp;&nbsp;"FileSize": 248837,<br />
   * &nbsp;&nbsp;&nbsp;&nbsp;"KeyID": "W39OAD3YGLCWAQKV1D6PGEKNW4RLGVTZTUWLYEVFQ2QG8AOCXW",<br />
   * &nbsp;&nbsp;&nbsp;&nbsp;"CreateUserID": "LEMONADMIN",<br />
   * &nbsp;&nbsp;&nbsp;&nbsp;"CreateDate": "2020-08-24T11:54:04.307Z",<br />
   * &nbsp;&nbsp;&nbsp;&nbsp;"UserName": "Quản trị hệ thống"<br />
   * }, ...]
   */
  data: PropTypes.array,
  /** The message to display when deleting files. */
  deleteNotifyText: PropTypes.string,
  /** If `true`, the component is disabled. */
  disabled: PropTypes.bool,
  /** Domain of attachments. */
  domain: PropTypes.string,
  /** Error of attachment. */
  error: PropTypes.string,
  /** Suggested passage attached. */
  hintText: PropTypes.string,
  /** [Attributes](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#attributes) applied to the input element. */
  inputProps: PropTypes.object,
  /** If `true`, strip domain of attachments. */
  isStripDomain: PropTypes.bool,
  /** Label of the component. */
  label: PropTypes.string,
  /** Limit the number of uploaded files. */
  maxFile: PropTypes.number,
  /** Limit the size of uploaded files. */
  maxSize: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Allows to attach multiple files. */
  multiple: PropTypes.bool,
  /** Callback fired when the files attached is changed. */
  onChange: PropTypes.func,
  /** Download attached event, if not it will use default. */
  onDownload: PropTypes.func,
  /**
   * event when removed file(s)
   *
   * return data: {<br/>
   * &nbsp;&nbsp;&nbsp;&nbsp;attached: [Files] (insist old and all new files )<br/>
   * &nbsp;&nbsp;&nbsp;&nbsp;allNewAttached: [Files]<br/>
   * &nbsp;&nbsp;&nbsp;&nbsp;oldAttached: [Files]<br/>
   * &nbsp;&nbsp;&nbsp;&nbsp;removedAttached: [Files]<br/>
   * &nbsp;&nbsp;&nbsp;&nbsp;element: [NodeList (just removed)]<br/>
   * }
   */
  onRemove: PropTypes.func,
  /** View attached event, if not it will use default. */
  onView: PropTypes.func,
  /** Name of the person who attached the file when uploading the file. */
  ownerName: PropTypes.string,
  /** At least one attachment is required. */
  required: PropTypes.bool,
  /** Style inline of component. */
  style: PropTypes.object,
  /** Unit size name, default auto-detect. */
  unitSize: PropTypes.string,
  /** Errors show up when the file upload is corrupted. */
  uploadErrorInfo: PropTypes.object,
  /** The view type of files. */
  viewType: PropTypes.oneOf(['detail', 'icon'])
};
export default Attachment;